import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import workspaceApi from '../utils/workspaceApi';
import './HubPages.css';

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const QUICK_LINKS = [
  { label: 'Inbox', route: '/inbox', icon: '📧' },
  { label: 'Calendar', route: '/calendar', icon: '📅' },
  { label: 'Tasks', route: '/tasks', icon: '✅' },
  { label: 'Notes', route: '/notes', icon: '📝' },
  { label: 'AI Assistant', route: '/ai-assistant', icon: '🤖' },
  { label: 'Storage', route: '/storage', icon: '💾' },
  { label: 'Compose', route: '/compose?ai=1', icon: '✨' },
  { label: 'Contacts', route: '/contacts', icon: '👥' },
];

/**
 * Workspace dashboard — unread mail, meetings, tasks, storage, AI usage.
 */
const Dashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await workspaceApi.getSummary();
      if (res?.success) {
        setSummary(res.data || {});
      } else {
        setError(res?.message || 'Failed to load dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const storagePct = Math.round(summary?.storageUsedPercent || 0);

  const tiles = [
    { key: 'mail', value: summary?.unreadEmails ?? 0, label: 'Unread mail', route: '/inbox' },
    { key: 'meetings', value: summary?.todaysMeetings ?? 0, label: "Today's meetings", route: '/calendar' },
    { key: 'tasks', value: summary?.pendingTasks ?? 0, label: 'Pending tasks', route: '/tasks' },
    { key: 'ai', value: summary?.aiUsage ?? 0, label: 'AI actions today', route: '/ai-assistant' },
    { key: 'drafts', value: summary?.drafts ?? 0, label: 'Drafts', route: '/drafts' },
  ];

  return (
    <div className="hub-page">
      <div className="hub-header">
        <div>
          <h1>Dashboard</h1>
          <p>
            Welcome{currentUser?.firstName ? `, ${currentUser.firstName}` : ''} — your workspace at a glance
          </p>
        </div>
        <button type="button" className="hub-secondary-btn" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="hub-body">
        {loading && <div className="hub-state">Loading dashboard…</div>}
        {!loading && error && <div className="hub-state hub-error">{error}</div>}
        {!loading && !error && (
          <>
            <div className="hub-stats-grid">
              {tiles.map((tile) => (
                <button
                  key={tile.key}
                  type="button"
                  className="hub-stat-tile"
                  onClick={() => navigate(tile.route)}
                >
                  <div className="hub-stat-value">{tile.value}</div>
                  <div className="hub-stat-label">{tile.label}</div>
                </button>
              ))}
              <button
                type="button"
                className="hub-stat-tile"
                onClick={() => navigate('/storage')}
              >
                <div className="hub-stat-value">{storagePct}%</div>
                <div className="hub-stat-label">
                  Storage · {formatBytes(summary?.storageUsedBytes)}
                </div>
                <div className={`hub-stat-bar ${storagePct >= 80 ? 'is-warning' : ''}`}>
                  <span style={{ width: `${Math.min(100, storagePct)}%` }} />
                </div>
              </button>
            </div>

            <div className="hub-quick-open">
              <h2>Quick open</h2>
              <div className="hub-chip-links">
                {QUICK_LINKS.map((link) => (
                  <button
                    key={link.route}
                    type="button"
                    className="hub-chip-link hub-chip-link-plain"
                    onClick={() => navigate(link.route)}
                  >
                    <span aria-hidden="true">{link.icon}</span> {link.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
